import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    Col,
    Dropdown,
    DropdownButton,
    Form,
    Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
    AuthedLayout,
    EditableTableDiv,
    HeaderDisplayKeysWithType,
} from '../../../components';
import { useClassGrade, useSemGrade } from '../../../hook';
import { StudentGradeNoSeat, StudentDisease, UserInfo } from '../../../model';
import apis from '../../../apis';
import {
    downloadDataAsExcel,
    printDataAsTable,
    SheetHeaderDisplay,
} from '../../../utils';
import { toast } from 'react-toastify';
type PageData = StudentDisease & StudentGradeNoSeat;

const tableHeader: (SheetHeaderDisplay<PageData> &
    HeaderDisplayKeysWithType<PageData>)[] = [
        { display: '年', property: 'grade' },
        {
            display: '班級',
            property: 'className',
        },
        { display: '座號', property: 'seat' },
        { display: '姓名', property: 'name' },
        { display: '疾病', property: 'diseaseName' },
        { display: '陳述', property: 'description' },
        { display: '照護', property: 'treatment' },
    ];

const mapState = (app: ApplicationState) => ({ ...app.auth });
const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const diseaseClass: FunctionComponent<Props> = ({
    user,
    catchErrorForModal,
}) => {
    const { currentSemester = '', semesters = {} } = user || {};
    const [currentSem, setCurrentSem] = useState(currentSemester);
    const { year = -1, sem = -1 } = semesters[currentSem];
    const yearSem = { year: year, sem: sem };
    const { selectedClassGrade: classGrade, element: classGradeElement } =
        useClassGrade({
            yearSem
        });
    const [students, setStudents] = useState<PageData[]>([]);
    const [onlySchoolCreated, setOnlySchoolCreated] = useState<boolean>(true);
    const [userinfo, setUser] = useState<UserInfo>();
    useEffect(() => {
        if (yearSem && classGrade) {
            apis.getUserInfo()
                .then((u) => {
                    setUser(u);
                })
                .catch(catchErrorForModal);

            toast
                .promise(
                    apis.getClassDisease(yearSem.year, yearSem.sem, onlySchoolCreated, classGrade.id)
                        .then((r) =>
                            setStudents(
                                r.map(({ studentDisease, ...s }) => ({
                                    ...studentDisease,
                                    ...s,
                                }))
                            )
                        ),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )

        }
    }, [classGrade?.id, yearSem.year, yearSem.sem, onlySchoolCreated]);

    return (
        <AuthedLayout>
            <Row className="justify-content-between">
                <Col>
                    <Row>
                        <Col xs={3} className="mr-3">
                            {classGradeElement}
                        </Col>
                    </Row>
                </Col>
                <Col xs={3} className="mr-3">
                    <Form.Check
                        type="checkbox"
                        id="chk-only-school-created"
                        label="只顯示本校建置資料"
                        checked={onlySchoolCreated}
                        onChange={(e) => setOnlySchoolCreated(e.target.checked)}
                    />
                </Col>
                <div className="d-flex">
                    {/* 所有班級 */}
                    <DropdownButton
                        id="disease-class-all-export"
                        title="所有班級"
                        variant="info"
                        className="mr-3"
                        disabled={!students.length}
                    >
                        <Dropdown.Item
                            onClick={() => {
                                apis
                                    .getClassDisease(
                                        yearSem.year,
                                        yearSem.sem,
                                        onlySchoolCreated
                                    )
                                    .then((r) => {
                                        const allStudents = r.map(
                                            ({ studentDisease, ...s }) => ({
                                                ...studentDisease,
                                                ...s,
                                            })
                                        );

                                        downloadDataAsExcel({
                                            title:
                                                `${user.schools[user.currentSchool].name}` +
                                                `${yearSem.year}學年` +
                                                `${yearSem.sem}學期_個人疾病史清單(分班)`,
                                            values: allStudents,
                                            headers: tableHeader,
                                            groupBy: (v) =>
                                                `${v.grade}年${v.no}班`,
                                            footer:
                                                '承辦人:          組長:          主任:          校長:',
                                            exportfile:
                                                userinfo?.exportFileType,
                                        });
                                    })
                                    .catch(catchErrorForModal);
                            }}
                        >
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={() => {
                                apis
                                    .getClassDisease(
                                        yearSem.year,
                                        yearSem.sem,
                                        onlySchoolCreated
                                    )
                                    .then((r) => {
                                        const allStudents = r.map(
                                            ({ studentDisease, ...s }) => ({
                                                ...studentDisease,
                                                ...s,
                                            })
                                        );

                                        printDataAsTable({
                                            title:
                                                `${user.schools[user.currentSchool].name}` +
                                                `${yearSem.year}學年` +
                                                `${yearSem.sem}學期_個人疾病史清單(分班)`,
                                            values: allStudents,
                                            headers: tableHeader,
                                            groupBy: (v) =>
                                                `${v.grade}年${v.no}班`,
                                            footer:
                                                '承辦人:          組長:          主任:          校長:',
                                        });
                                    })
                                    .catch(catchErrorForModal);
                            }}
                        >
                            列印
                        </Dropdown.Item>
                    </DropdownButton>

                    {/* 單一班級 */}
                    <DropdownButton
                        id="disease-class-single-export"
                        title="單一班級"
                        variant="success"
                        disabled={!students.length}
                    >
                        <Dropdown.Item
                            onClick={() => {
                                downloadDataAsExcel({
                                    title:
                                        `${user.schools[user.currentSchool].name}` +
                                        `${yearSem.year}學年` +
                                        `${yearSem.sem}學期_個人疾病史清單(分班)`,
                                    values: students,
                                    headers: tableHeader,
                                    groupBy: () =>
                                        `${classGrade?.grade}年${classGrade?.no}班`,
                                    footer:
                                        '承辦人:          組長:          主任:          校長:',
                                    exportfile:
                                        userinfo?.exportFileType,
                                });
                            }}
                        >
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={() => {
                                printDataAsTable({
                                    title:
                                        `${user.schools[user.currentSchool].name}` +
                                        `${yearSem.year}學年` +
                                        `${yearSem.sem}學期_個人疾病史清單(分班)`,
                                    values: students,
                                    headers: tableHeader,
                                    groupBy: () =>
                                        `${classGrade?.grade}年${classGrade?.no}班`,
                                    footer:
                                        '承辦人:          組長:          主任:          校長:',
                                });
                            }}
                        >
                            列印
                        </Dropdown.Item>
                    </DropdownButton>
                </div>
            </Row>
            <hr />
            <Row>
                <EditableTableDiv
                    headers={tableHeader}
                    values={students}
                />
            </Row>
        </AuthedLayout>
    );
};

export const DiseaseClass = connector(diseaseClass);